import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GhostIcon } from './HalloweekLink';
const FLICKER_EVERY_MS = 16000;
const FIRST_FLICKER_MS = 6000;
const SPIDER_EVERY_MS = 11000;

/**
 * Page-wide spooky layer for the home page:
 * - "Lights flicker": the page dims and half of it turns Halloween orange,
 *   while a ghost drifts across.
 * - A spider that now and then drops down on its thread.
 * Purely decorative; never blocks clicks.
 */
export function SpookyAtmosphere() {
  const reduceMotion = useReducedMotion();
  const [flicker, setFlicker] = useState(0);
  const [spider, setSpider] = useState<{
    id: number;
    left: number;
  } | null>(null);
  useEffect(() => {
    if (reduceMotion) return;
    const timers: number[] = [];
    const runFlicker = () => {
      setFlicker(n => n + 1);
      timers.push(window.setTimeout(() => setFlicker(0), 1600));
    };
    timers.push(window.setTimeout(runFlicker, FIRST_FLICKER_MS));
    const flickerInterval = window.setInterval(runFlicker, FLICKER_EVERY_MS);
    let spiderId = 0;
    const dropSpider = () => {
      const id = ++spiderId;
      setSpider({
        id,
        left: 12 + Math.random() * 76
      });
      timers.push(window.setTimeout(() => setSpider(s => s?.id === id ? null : s), 4200));
    };
    timers.push(window.setTimeout(dropSpider, 3000));
    const spiderInterval = window.setInterval(dropSpider, SPIDER_EVERY_MS);
    return () => {
      timers.forEach(t => window.clearTimeout(t));
      window.clearInterval(flickerInterval);
      window.clearInterval(spiderInterval);
    };
  }, [reduceMotion]);
  if (reduceMotion) return null;
  return <div className="pointer-events-none fixed inset-0 z-40" aria-hidden="true" data-id="element-1267" data-mp="2xflj986v7uql">
      {/* Lights flicker + half the page turns orange */}
      <AnimatePresence data-id="element-1268" data-mp="1q32wp7u1lsoc">
        {flicker > 0 && <motion.div key={flicker} className="absolute inset-0" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.3
        }
      }} data-id="element-1269" data-mp="1pzox27crjxkl">
            <motion.div className="absolute inset-0 bg-[#0E0C0B]" animate={{
          opacity: [0, 0.55, 0.1, 0.65, 0.2, 0.45, 0]
        }} transition={{
          duration: 1.5,
          ease: 'linear'
        }} data-id="element-1270" data-mp="1sgya9gn7hth5" />
            <motion.div className="absolute inset-y-0 right-0 w-1/2 bg-[#E8650F] mix-blend-multiply" animate={{
          opacity: [0, 0.5, 0.15, 0.55, 0]
        }} transition={{
          duration: 1.5,
          ease: 'linear'
        }} data-id="element-1271" data-mp="1igxq5shij9bw" />
            <motion.div className="absolute top-1/3" initial={{
          x: '-20vw',
          opacity: 0
        }} animate={{
          x: '110vw',
          opacity: [0, 0.9, 0.9, 0],
          y: [0, -30, 10, -20]
        }} transition={{
          duration: 1.5,
          ease: [0.45, 0, 0.55, 1]
        }} data-id="element-1272" data-mp="ut6aqg0oeg6e">
              <GhostIcon className="w-28 h-28 md:w-40 md:h-40 opacity-90" data-id="element-1273" data-mp="ptlfogommryy" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Dropping spider */}
      <AnimatePresence data-id="element-1274" data-mp="77dtpemwnvnm">
        {spider && <motion.div key={spider.id} className="absolute top-0 flex flex-col items-center origin-top" style={{
        left: `${spider.left}%`
      }} initial={{
        y: -140
      }} animate={{
        y: 0,
        rotate: [0, 4, -4, 2, 0]
      }} exit={{
        y: -140,
        transition: {
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }
      }} transition={{
        y: {
          type: 'spring',
          stiffness: 120,
          damping: 9
        },
        rotate: {
          duration: 2.4,
          ease: 'easeInOut'
        }
      }} data-id="element-1275" data-mp="27hrnkxrqeap">
            <span className="block w-px h-24 md:h-36 bg-[#1A1412]/50" data-id="element-1276" data-mp="yytgb8ogpidu" />
            <Spider data-id="element-1277" data-mp="uwewal924ckz" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function Spider() {
  return <svg viewBox="0 0 40 36" className="w-9 h-8 md:w-11 md:h-10 -mt-1" aria-hidden="true" data-id="element-1278" data-mp="18v6byc3ki6b8">
      <g stroke="#1A1412" strokeWidth="2" strokeLinecap="round" fill="none" data-id="element-1279" data-mp="13xmvd5jedhg4">
        <path d="M14 16 6 10 2 14M14 19 4 18 1 23M15 22 7 26 5 32" data-id="element-1280" data-mp="1nx4z6rff1ukx" />
        <path d="M26 16 34 10 38 14M26 19 36 18 39 23M25 22 33 26 35 32" data-id="element-1281" data-mp="1ryzyxrhp7zwl" />
      </g>
      <ellipse cx="20" cy="20" rx="7" ry="8" fill="#1A1412" data-id="element-1282" data-mp="1wq87klb7fh8p" />
      <circle cx="20" cy="11" r="4.5" fill="#1A1412" data-id="element-1283" data-mp="1wqropzt161lo" />
      <circle cx="18.4" cy="10.6" r="1.1" fill="#FF9A4D" data-id="element-1284" data-mp="21puketn4evgz" />
      <circle cx="21.6" cy="10.6" r="1.1" fill="#FF9A4D" data-id="element-1285" data-mp="21rffckdckgfd" />
    </svg>;
}